import {
	AddIcon,
	Button,
	CopyIcon,
	DeleteIcon,
	Input,
	Modal,
	ModuleHeader,
	PieChartIcon,
	SearchIcon,
	SpinnerWithBackground,
	Text,
} from '@viapeople/components'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useModulePermission } from '../../hooks/useModulePermission'
import { sessionStore } from '../../sharedStores/sessionStore'
import { useToast } from '../../utils/ToastContext'
import type { PecDashboardStore } from './stores/pecDashboardStore'
import type { PecCycleSearchResult } from './types'

interface PecPageProps {
	store: () => PecDashboardStore
}

type PecCycleListItem = PecCycleSearchResult['data'][0]

type SearchParams = {
	search?: string
	status?: 'draft' | 'ready' | 'active' | 'closed' | 'archived'
	sortBy?: 'name' | 'createdAt' | 'updatedAt' | 'launchedAt'
	order?: 'asc' | 'desc'
	page?: number
	perPage?: number
}

export const PecPage = ({ store }: PecPageProps) => {
	const {
		pecCycles,
		selectedCycle,
		isLoading,
		error,
		searchQuery,
		initialize,
		search,
		duplicateSelectedCycle,
		deleteSelectedCycle,
		setSelectedCycle,
		setSearchQuery,
	} = store()

	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false)
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
	const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('')
	const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

	// Check PEC module permission
	const { isLoading: isCheckingPermission, isAuthorized, role } = useModulePermission('pec')
	const isViewerAdminOnly = role === 'viewerAdmin'

	// Redirect unauthorized users
	useEffect(() => {
		if (!isCheckingPermission && !isAuthorized) {
			toast.showToast('You do not have permission to access the PEC module', 'error')
			navigate('/')
		}
	}, [isCheckingPermission, isAuthorized, navigate, toast])

	useEffect(() => {
		if (organizationId) {
			initialize(organizationId)
		}
	}, [organizationId, initialize])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
		}
	}, [error, toast])

	const handleOpenDuplicateModal = (cycle: PecCycleListItem) => {
		setSelectedCycle(cycle)
		setIsDuplicateModalOpen(true)
	}

	const handleOpenDeleteModal = (cycle: PecCycleListItem) => {
		setSelectedCycle(cycle)
		setDeleteConfirmationInput('')
		setIsDeleteModalOpen(true)
	}

	const handleDuplicate = async () => {
		try {
			await duplicateSelectedCycle()
			toast.showToast('PEC cycle duplicated successfully')
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to duplicate PEC cycle'
			toast.showToast(message, 'error')
		} finally {
			setIsDuplicateModalOpen(false)
		}
	}

	const handleDelete = async () => {
		try {
			await deleteSelectedCycle()
			toast.showToast('PEC cycle deleted successfully')
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to delete PEC cycle'
			toast.showToast(message, 'error')
		} finally {
			setIsDeleteModalOpen(false)
		}
	}

	const handleRowClick = (cycle: PecCycleListItem) => {
		navigate(`/pec/${cycle.id}/general`)
	}

	// Handle external search changes with debouncing
	const handleSearchChange = useCallback(
		(value: string) => {
			setSearchQuery(value)

			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			searchTimeoutRef.current = setTimeout(() => {
				if (organizationId) {
					search({ organizationId })
				}
			}, 300)
		},
		[organizationId, setSearchQuery, search],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	// Query options generator for GenericTable
	const queryOptionsGenerator = useCallback((_options: SearchParams) => {
		return { enabled: false }
	}, [])

	// Create conditional empty state based on search context
	const getEmptyState = useMemo(() => {
		if (searchQuery.trim().length > 0) {
			return {
				icon: SearchIcon,
				title: 'No PEC cycles match your search',
				description: 'Try adjusting your search terms or clear the search to see all cycles',
			}
		}

		return {
			icon: PieChartIcon,
			title: 'No PEC cycles created yet',
			description: 'Get started by creating your first PEC cycle',
			actionLabel: 'Create PEC Cycle',
			onAction: () => navigate('/pec/create/general'),
		}
	}, [searchQuery, navigate])

	// Show spinner while checking permissions or loading data
	if (isCheckingPermission || isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	// Don't render if not authorized (redirect will happen via useEffect)
	if (!isAuthorized) {
		return null
	}

	const formatStatus = (status: string) => {
		return status.charAt(0).toUpperCase() + status.slice(1)
	}

	const columns: Column<PecCycleListItem>[] = [
		{
			field: 'name',
			label: 'Name',
			colSpan: 2,
			sortable: true,
			render: (cycle: PecCycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{cycle.name}
				</Text>
			),
		},
		{
			field: 'status',
			label: 'Status',
			colSpan: 1,
			sortable: true,
			render: (cycle: PecCycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{formatStatus(cycle.status)}
				</Text>
			),
		},
		{
			field: 'ratingScale',
			label: 'Rating Scale',
			colSpan: 1,
			sortable: false,
			render: (cycle: PecCycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{cycle.ratingScale} Point
				</Text>
			),
		},
		{
			field: 'launchedAt',
			label: 'Launched',
			colSpan: 1,
			sortable: true,
			render: (cycle: PecCycleListItem) => {
				const isValidDate = cycle.launchedAt
				return (
					<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
						{isValidDate ? new Date(isValidDate).toLocaleDateString() : '-'}
					</Text>
				)
			},
		},
		{
			field: 'participantCount',
			label: 'Self Evaluators',
			colSpan: 1,
			sortable: false,
			render: (cycle: PecCycleListItem) => (
				<Text variant='xsmall' weight='regular' className='group-hover:text-primary-600'>
					{cycle.participantCount || 0}
				</Text>
			),
		},
		{
			field: 'respondersCount',
			label: 'Responses',
			colSpan: 1,
			sortable: false,
			render: (cycle: PecCycleListItem) => {
				const respondersCount = (cycle as any).respondersCount || 0
				const participantCount = cycle.participantCount || 0
				const percentage = participantCount > 0 ? ((respondersCount / participantCount) * 100).toFixed(1) : '0.0'
				return (
					<div className='flex flex-col'>
						<Text variant='xsmall' weight='semibold' className='group-hover:text-primary-600'>
							{respondersCount}
						</Text>
						{participantCount > 0 && (
							<Text variant='xsmall' className='text-gray-500'>
								{percentage}%
							</Text>
						)}
					</div>
				)
			},
		},
		...(!isViewerAdminOnly
			? [
					{
						field: 'id' as const,
						label: 'Actions',
						colSpan: 1,
						sortable: false,
						render: (cycle: PecCycleListItem) => (
							// biome-ignore lint/a11y/noStaticElementInteractions: presentation container for event bubbling
							<div
								role='presentation'
								className='flex justify-start gap-2'
								onClick={(e) => e.stopPropagation()}
								onKeyDown={(e) => e.stopPropagation()}>
								<Button
									variant='secondary'
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										handleOpenDuplicateModal(cycle)
									}}
									title='Duplicate cycle'>
									<CopyIcon />
								</Button>
								<Button
									variant='secondary'
									size='small'
									onClick={(e) => {
										e.stopPropagation()
										handleOpenDeleteModal(cycle)
									}}
									title='Delete cycle'>
									<DeleteIcon />
								</Button>
							</div>
						),
					},
				]
			: []),
	]

	return (
		<main className='p-8 pl-12'>
			<ModuleHeader breadcrumb={[{ label: 'Home', to: '/' }, { label: 'PEC' }]} title='PEC Cycles' />

			<GenericTable<PecCycleListItem, SearchParams>
				data={pecCycles}
				columns={columns}
				onRowClick={handleRowClick}
				externalDataFetching={true}
				searchValue={searchQuery}
				onSearchChange={handleSearchChange}
				queryOptionsGenerator={queryOptionsGenerator}
				actionButtons={
					isViewerAdminOnly
						? []
						: [
								{
									children: 'Create PEC Cycle',
									icon: AddIcon,
									onClick: () => navigate('/pec/create/general'),
									variant: 'primary',
								},
							]
				}
				emptyState={getEmptyState}
			/>

			{/* Duplicate Modal */}
			<Modal
				isOpen={isDuplicateModalOpen}
				onClose={() => setIsDuplicateModalOpen(false)}
				title='Duplicate PEC Cycle'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDuplicateModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'primary',
						onClick: handleDuplicate,
						children: 'Duplicate',
					},
				]}>
				<Text variant='small'>
					Are you sure you want to duplicate <strong>"{selectedCycle?.name}"</strong>?
				</Text>
			</Modal>

			{/* Delete Modal */}
			<Modal
				isOpen={isDeleteModalOpen}
				onClose={() => setIsDeleteModalOpen(false)}
				title='Delete PEC Cycle'
				buttons={[
					{
						variant: 'secondary',
						onClick: () => setIsDeleteModalOpen(false),
						children: 'Cancel',
					},
					{
						variant: 'destructive',
						onClick: handleDelete,
						children: 'Delete',
						disabled: selectedCycle?.status !== 'draft' && deleteConfirmationInput !== selectedCycle?.name,
					},
				]}>
				<div className='space-y-4'>
					<Text variant='small'>
						Are you sure you want to delete <strong>"{selectedCycle?.name}"</strong>? This action cannot be undone.
					</Text>
					{selectedCycle?.status !== 'draft' && (
						<div className='bg-amber-50 border border-amber-200 rounded-lg p-3'>
							<Text variant='xsmall' className='text-amber-800 mb-2'>
								This cycle is {selectedCycle?.status}. Deleting it will permanently remove all associated data including
								participant responses.
							</Text>
							<Input
								label='Type the cycle name to confirm'
								placeholder={selectedCycle?.name}
								value={deleteConfirmationInput}
								onChange={(e) => setDeleteConfirmationInput(e.target.value)}
							/>
						</div>
					)}
				</div>
			</Modal>
		</main>
	)
}
